import { useState } from 'react';
import { Plus, Copy, Printer, X } from 'lucide-react';
import { toast } from 'sonner';
import { TickerPicker, ErrorNote, API_URL, fetchJson, inputCls } from './shared';

const ENGINE_NAMES = { energy: 'Energy', pressure: 'Pressure', position: 'Position', momentum: 'Momentum', environment: 'Environment' };

const buildItems = (d) => {
  const items = [
    `Trade state reads "${d.trade_state}" (confidence: ${d.confidence?.label || 'n/a'})`,
    ...Object.entries(d.engines || {}).map(([k, e]) => `${ENGINE_NAMES[k] || k} engine reads "${e.label}"`),
    d.what_to_monitor && `Monitor: ${d.what_to_monitor}`,
    ...(d.false_move?.flags || []).map((f) => `Structural flag: ${f}`),
    d.volatility?.level && `Volatility is ${d.volatility.level.toLowerCase()} (${d.volatility.comparison || ''})`.replace(' ()', ''),
  ].filter(Boolean);
  return items.map((text, i) => ({ id: `a${i}`, text, done: false }));
};

export const ConditionsChecklist = ({ ready }) => {
  const [market, setMarket] = useState('');
  const [ticker, setTicker] = useState('');
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [custom, setCustom] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const build = async () => {
    setLoading(true); setError(null);
    try {
      const d = await fetchJson(`${API_URL}/api/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: ticker.trim(), market, include_ai: false }),
      });
      setItems((it) => [...buildItems(d), ...it.filter((x) => x.id.startsWith('c'))]);
      setMeta({ ticker: d.ticker, market: d.market, at: d.analysis_timestamp, name: d.asset_name });
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const addCustom = () => {
    if (!custom.trim()) return;
    setItems((it) => [...it, { id: `c${Date.now()}`, text: custom.trim(), done: false }]);
    setCustom('');
  };
  const toggle = (id) => setItems((it) => it.map((x) => (x.id === id ? { ...x, done: !x.done } : x)));
  const remove = (id) => setItems((it) => it.filter((x) => x.id !== id));
  const asText = () => [`Trade PMI conditions checklist${meta ? `: ${meta.ticker} (${meta.market})` : ''}`, ...items.map((x) => `[${x.done ? 'x' : ' '}] ${x.text}`), '', 'Descriptive data only. Not financial advice.'].join('\n');
  const copy = async () => {
    try { await navigator.clipboard.writeText(asText()); toast.success('Checklist copied'); } catch { toast.error('Copy not available in this browser'); }
  };

  return (
    <div className="space-y-5" data-testid="checklist-tool">
      <TickerPicker prefix="checklist" market={market} setMarket={setMarket} ticker={ticker} setTicker={setTicker}
        onSubmit={build} loading={loading} disabled={!ready || !market || !ticker.trim()} cta="Build checklist" />
      <p className="text-[11px] text-zinc-600">Generate items from the latest five-engine read, or just add your own below.</p>
      <ErrorNote error={error} testId="checklist-error" />

      <div className="print-area space-y-3">
        {meta && (
          <p className="text-xs text-zinc-400" data-testid="checklist-meta">
            <span className="font-mono text-white">{meta.ticker}</span> · {meta.market} · read at {new Date(meta.at).toLocaleString()}
          </p>
        )}
        <ul className="space-y-1.5" data-testid="checklist-items">
          {items.map((x, i) => (
            <li key={x.id} className="flex items-start gap-3 bg-zinc-900/30 border border-zinc-800/60 rounded-md px-3 py-2.5 group" data-testid={`checklist-item-${i}`}>
              <input type="checkbox" checked={x.done} onChange={() => toggle(x.id)} className="mt-0.5 w-4 h-4 accent-brand flex-shrink-0" data-testid={`checklist-item-check-${i}`} />
              <span className={`text-sm flex-1 ${x.done ? 'line-through text-zinc-600' : 'text-zinc-300'}`}>{x.text}</span>
              <button onClick={() => remove(x.id)} className="text-zinc-600 hover:text-red-400 no-print" aria-label="Remove item" data-testid={`checklist-item-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex gap-2 no-print">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCustom()}
          placeholder="Add your own condition to check..." maxLength={160} className={inputCls} data-testid="checklist-custom-input" />
        <button onClick={addCustom} className="border border-zinc-700 hover:border-brand/60 px-3 rounded-md text-zinc-300" aria-label="Add item" data-testid="checklist-add-btn">
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {items.length > 0 && (
        <div className="flex gap-3 no-print">
          <button onClick={copy} className="text-xs border border-zinc-700 hover:border-brand/60 text-zinc-300 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5" data-testid="checklist-copy-btn">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button onClick={() => window.print()} className="text-xs border border-zinc-700 hover:border-brand/60 text-zinc-300 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5" data-testid="checklist-print-btn">
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          <span className="text-[11px] text-zinc-600 self-center" data-testid="checklist-progress">{items.filter((x) => x.done).length}/{items.length} reviewed</span>
        </div>
      )}
    </div>
  );
};
